import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Reveal } from "../components/Reveal";

const CATEGORIES = ["All", "Tees", "Hoodies", "Outerwear", "Headwear"];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("new");
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "All";
  const customOnly = params.get("custom") === "1";

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: { category, q, sort } })
      .then(({ data }) => setProducts(data))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [category, q, sort]);

  const visible = useMemo(() => (customOnly ? products.filter((p) => p.customizable) : products), [products, customOnly]);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-12 pt-32 pb-10" data-testid="shop-page">
      <Reveal>
        <p className="eyebrow mb-3">{customOnly ? "Custom studio" : "Catalog"}</p>
        <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
          {customOnly ? "Build your piece" : "All apparel"}
        </h1>
      </Reveal>

      <div className="mt-12 flex flex-col lg:flex-row lg:items-center gap-4 justify-between border-y border-white/10 py-4">
        <div className="flex flex-wrap gap-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase()}`}
              onClick={() => setParams((p) => { const n = new URLSearchParams(p); c === "All" ? n.delete("category") : n.set("category", c); return n; })}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-2 border transition-colors active:scale-95 ${
                category === c ? "bg-kinetic border-kinetic text-white" : "border-white/15 text-zinc-400 hover:border-kinetic hover:text-kinetic"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="shop-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="SEARCH"
              className="input-dark pl-9 !py-2 font-mono text-xs uppercase tracking-widest w-44"
            />
          </div>
          <select
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-surface border border-white/10 font-mono text-xs uppercase tracking-widest px-3 py-2 text-zinc-300 focus:outline-none focus:ring-2 focus:ring-kinetic"
          >
            <option value="new">Newest</option>
            <option value="price_asc">Price ↑</option>
            <option value="price_desc">Price ↓</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 py-24 text-center" data-testid="shop-loading">Loading the racks…</p>
      ) : visible.length === 0 ? (
        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 py-24 text-center" data-testid="shop-empty">Nothing matches. Try another filter.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="shop-grid">
          {visible.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
